import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { TableSurface, headerCellSx, bodyRowSx, TableEmptyState } from '../common/pageKit';
import StatementAmount from './StatementAmount';

/**
 * Hierarchical statement table for statutory reports — Balance Sheet, P&L,
 * Cash Flow. Renders section headers, indented line items, a bold sub-total
 * per section and an optionally separated grand total, with right-aligned
 * tabular-nums amounts throughout.
 *
 * A flat list of amounts reads as a spreadsheet dump, not a financial
 * statement — the section/sub-total/grand-total structure here is what makes
 * the page read as authoritative.
 */

export interface StatementLine {
  id: string | number;
  label: string;
  current?: number | null;
  comparative?: number | null;
  /** 'group' renders a label-only sub-heading inside a section — no amount cells. */
  kind?: 'item' | 'group';
}

export interface StatementSection {
  id: string | number;
  name: string;
  items: StatementLine[];
  /** Section sub-total. Omit if the section has no computable total. */
  total?: number | null;
  comparativeTotal?: number | null;
  /** Defaults to "Sub Total". */
  totalLabel?: string;
}

interface StatementTableProps {
  sections: StatementSection[];
  showComparative?: boolean;
  showVariance?: boolean;
  currentLabel?: string;
  comparativeLabel?: string;
  grandTotalLabel?: string;
  grandTotal?: number | null;
  grandComparativeTotal?: number | null;
  /** Hex accent for section headers and the grand total band. */
  accent?: string;
  emptyTitle?: string;
  emptyHint?: string;
}

const variancePercent = (current: number, comparative: number): number | null =>
  comparative !== 0 ? ((current - comparative) / Math.abs(comparative)) * 100 : null;

const StatementTable: React.FC<StatementTableProps> = ({
  sections,
  showComparative = false,
  showVariance = false,
  currentLabel = 'Amount',
  comparativeLabel = 'Prior Period',
  grandTotalLabel,
  grandTotal,
  grandComparativeTotal,
  accent = '#0284c7',
  emptyTitle = 'No data for this statement',
  emptyHint = 'Generate the report to see figures here.',
}) => {
  const varianceActive = showVariance && showComparative;
  const columnCount = 1 + 1 + (showComparative ? 1 : 0) + (varianceActive ? 1 : 0);
  const hasContent = sections.some((s) => s.items.length > 0);

  const computeVariance = (current?: number | null, comparative?: number | null) => {
    if (typeof current !== 'number' || typeof comparative !== 'number') return { amount: null, percent: null };
    const amount = current - comparative;
    return { amount, percent: variancePercent(current, comparative) };
  };

  return (
    <TableSurface>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Particulars</TableCell>
              <TableCell sx={{ ...headerCellSx, whiteSpace: 'nowrap' }} align="right">
                {currentLabel}
              </TableCell>
              {showComparative && (
                <TableCell sx={{ ...headerCellSx, whiteSpace: 'nowrap' }} align="right">
                  {comparativeLabel}
                </TableCell>
              )}
              {varianceActive && (
                <TableCell sx={{ ...headerCellSx, whiteSpace: 'nowrap' }} align="right">
                  Variance
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {!hasContent && (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ border: 0 }}>
                  <TableEmptyState title={emptyTitle} hint={emptyHint} />
                </TableCell>
              </TableRow>
            )}

            {sections.map((section) => (
              <React.Fragment key={section.id}>
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      bgcolor: 'action.hover',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1,
                    }}
                  >
                    {section.name}
                  </TableCell>
                </TableRow>

                {section.items.map((item) =>
                  item.kind === 'group' ? (
                    <TableRow key={item.id} sx={bodyRowSx}>
                      <TableCell
                        colSpan={columnCount}
                        sx={{ pl: 3, pt: 1.5, pb: 0.25, border: 0 }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                          {item.label}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={item.id} sx={bodyRowSx}>
                      <TableCell sx={{ pl: 4, border: 0 }}>
                        <Typography variant="body2">{item.label}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0 }}>
                        <StatementAmount value={item.current} />
                      </TableCell>
                      {showComparative && (
                        <TableCell align="right" sx={{ border: 0 }}>
                          <StatementAmount value={item.comparative} tone="neutral" />
                        </TableCell>
                      )}
                      {varianceActive &&
                        (() => {
                          const { amount, percent } = computeVariance(item.current, item.comparative);
                          return (
                            <TableCell align="right" sx={{ border: 0 }}>
                              <StatementAmount value={amount} tone="auto" />
                              {percent !== null && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
                                >
                                  {percent >= 0 ? '+' : ''}
                                  {percent.toFixed(1)}%
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })()}
                    </TableRow>
                  )
                )}

                {section.total !== undefined && (
                  <TableRow>
                    <TableCell
                      sx={{
                        pl: 3,
                        fontWeight: 700,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        borderBottom: 0,
                      }}
                    >
                      {section.totalLabel || 'Sub Total'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ borderTop: '1px solid', borderColor: 'divider', borderBottom: 0 }}
                    >
                      <StatementAmount value={section.total} bold />
                    </TableCell>
                    {showComparative && (
                      <TableCell
                        align="right"
                        sx={{ borderTop: '1px solid', borderColor: 'divider', borderBottom: 0 }}
                      >
                        <StatementAmount value={section.comparativeTotal} bold />
                      </TableCell>
                    )}
                    {varianceActive && (
                      <TableCell
                        align="right"
                        sx={{ borderTop: '1px solid', borderColor: 'divider', borderBottom: 0 }}
                      >
                        {(() => {
                          const { amount } = computeVariance(section.total, section.comparativeTotal);
                          return <StatementAmount value={amount} bold tone="auto" />;
                        })()}
                      </TableCell>
                    )}
                  </TableRow>
                )}
              </React.Fragment>
            ))}

            {grandTotal !== undefined && (
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.9375rem',
                    borderTop: '2px solid',
                    borderColor: accent,
                    bgcolor: `${accent}14`,
                  }}
                >
                  {grandTotalLabel || 'Total'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ borderTop: '2px solid', borderColor: accent, bgcolor: `${accent}14` }}
                >
                  <StatementAmount value={grandTotal} bold variant="body1" />
                </TableCell>
                {showComparative && (
                  <TableCell
                    align="right"
                    sx={{ borderTop: '2px solid', borderColor: accent, bgcolor: `${accent}14` }}
                  >
                    <StatementAmount value={grandComparativeTotal} bold variant="body1" />
                  </TableCell>
                )}
                {varianceActive && (
                  <TableCell
                    align="right"
                    sx={{ borderTop: '2px solid', borderColor: accent, bgcolor: `${accent}14` }}
                  >
                    {(() => {
                      const { amount } = computeVariance(grandTotal, grandComparativeTotal);
                      return <StatementAmount value={amount} bold tone="auto" variant="body1" />;
                    })()}
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </TableSurface>
  );
};

export default StatementTable;
